import React, { useState } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import PlaceIcon from "@mui/icons-material/Place"
import { useQuery } from "react-query"
import useStickyState from "../hooks/useStickyState"
import { listGyms, normalizeSlug } from "../utils/gymApi"

/**
 * "Display at gym…" button + dialog. Calls onConfirm(slug, slot) with the
 * chosen gym slug and display slot ("A" or "B").
 */
export default function GymSlotPicker({
  onConfirm,
  buttonLabel = "Display at gym…",
  disabled = false,
}) {
  const [open, setOpen] = useState(false)
  const [slot, setSlot] = useState("A")
  const [customSlug, setCustomSlug] = useState("")
  const [recentGyms] = useStickyState([], "recentGyms")
  const { data } = useQuery("gyms", listGyms, { enabled: open })

  const gyms = data?.gyms || []
  const recents = recentGyms || []
  const others = gyms.filter(
    (gym) => !recents.some((recent) => recent.slug === gym.slug)
  )

  const choose = (slug) => {
    setOpen(false)
    onConfirm(slug, slot)
  }

  const chooseCustom = () => {
    const slug = normalizeSlug(customSlug)
    if (slug) {
      choose(slug)
    }
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<PlaceIcon />}
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        {buttonLabel}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Display at gym</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="body2">As problem</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={slot}
              onChange={(_, value) => value && setSlot(value)}
            >
              <ToggleButton value="A">A</ToggleButton>
              <ToggleButton value="B">B</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {recents.length > 0 && (
            <>
              <Typography variant="subtitle2">Recent</Typography>
              <List dense>
                {recents.map((gym) => (
                  <ListItem key={gym.slug} disablePadding>
                    <ListItemButton onClick={() => choose(gym.slug)}>
                      <ListItemText primary={gym.name} secondary={gym.slug} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
          {others.length > 0 && (
            <>
              <Typography variant="subtitle2">All gyms</Typography>
              <List dense sx={{ maxHeight: "30vh", overflow: "auto" }}>
                {others.map((gym) => (
                  <ListItem key={gym.slug} disablePadding>
                    <ListItemButton onClick={() => choose(gym.slug)}>
                      <ListItemText primary={gym.name} secondary={gym.slug} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Other gym (creates it if new)"
              value={customSlug}
              onChange={(event) => setCustomSlug(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && chooseCustom()}
            />
            <Button variant="contained" onClick={chooseCustom}>
              Go
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
